import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import Swal from 'sweetalert2'
import IPButton from '../IPButton'
import IPInput from '../IPInput'
import IPRadio from '../IPRadio'
import IPFile from '../IPFile'
import InputMask from '../InputMask'
import '../IPModal/IPModal.scss'
import { handleImageUpload } from '../../base/utils/dataHelper'
import { GetRepresentativesApi } from '../../layouts/components/client/services/getRepresentativesApi'
import { IRepresentatives } from '../../layouts/components/client/models'
import {
  DEALER_CONTRACT_DOCUMENT_KEYS,
  DealerContractRejectScope,
  DealerContractStatus,
  IDealerContractFile,
  IDealerContractPopup,
} from '../../pages/Homepage/commonModels/dealerContractModels'
import {
  DealerContractConfirmApi,
  DealerContractReopenApi,
  DealerContractSaveInfoApi,
  DealerContractSendSmsApi,
  DealerContractUploadDocumentsApi,
} from '../../pages/Homepage/services/dealerContractApi'

interface DealerContractModalProps {
  /** Backend'den gelen popup durumu. null ise modal gösterilmez. */
  data: IDealerContractPopup | null
  /** Akış tamamlanıp siteye erişim açıldığında çağrılır. */
  onCompleted: () => void
  /**
   * Remote oturum (bir bayiye bağlanan MT/rapor kullanıcısı) için modalın atlanmasına izin verir.
   * Bayinin kendi oturumunda ASLA true olmamalı — modalın kapatılamaz olması buna bağlı.
   */
  allowSkip?: boolean
}

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png']

/**
 * Modal baslik gorseli. Dosya yoksa altindaki kirmizi gradient devreye girer,
 * yani gorsel eklenmeden de baslik duzgun gorunur.
 */
const HEADER_IMAGE = '/images/sozlesme-baslik.png'

/** Backend DealerContractAccessRule.NormalizeMobilePhone ile ayni mantik. */
const normalizePhone = (value: string): string => {
  let digits = (value ?? '').replace(/\D/g, '')
  if (digits.length > 10 && digits.startsWith('90')) digits = digits.substring(2)
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.substring(1)
  return digits
}

/**
 * Telefonu goruntuleme bicimine cevirir: "(212) 331 2366".
 * Gelen deger ham ("02123312366"), formatli veya ulke kodlu olabilir; hepsi ayni cikti verir.
 * 10 haneye normalize edilemeyen deger oldugu gibi gosterilir.
 */
const formatPhone = (value: string): string => {
  const digits = normalizePhone(value)
  if (digits.length !== 10) return (value ?? '').trim()
  return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)} ${digits.substring(6)}`
}
const MAX_FILE_SIZE_BYTE = 10 * 1024 * 1024

/**
 * Sözleşmesi onaylanmamış bayi için kapatılamaz modal.
 *
 * Adımlar backend'in ürettiği Step alanına göre: form -> contract -> (documents) -> wait.
 * Kapatma yok, ESC yok, dışarı tıklama yok, "Siteye Git" yok — akış tamamlanana kadar
 * bayi siteyi kullanamaz. Ödeme ve sipariş sunucu tarafında da ayrıca bloklu.
 *
 * Tek istisna allowSkip: remote oturumda modal "Geç" butonuyla atlanabilir.
 */
const DealerContractModal = (props: DealerContractModalProps) => {
  const { data } = props

  const [step, setStep] = useState<string>(data?.Step ?? 'form')
  const [contractFileUrl, setContractFileUrl] = useState<string | null>(data?.ContractFileUrl ?? null)
  const [busy, setBusy] = useState(false)

  // bilgi formu
  const [authorizedNameSurname, setAuthorizedNameSurname] = useState(data?.Info?.AuthorizedNameSurname ?? '')
  const [authorizedTc, setAuthorizedTc] = useState(data?.Info?.AuthorizedTc ?? '')
  const [mersisNo, setMersisNo] = useState(data?.Info?.MersisNo ?? '')
  const [hasNoMersis, setHasNoMersis] = useState(data?.Info?.HasNoMersis ?? false)
  const [commercialMessage, setCommercialMessage] = useState<number>(data?.Info?.CommercialMessage ? 1 : -1)
  const [email, setEmail] = useState(data?.Info?.Email ?? '')
  // Maske 10 haneye hizali calisir; gelen deger ulke kodlu ("905321234567") veya
  // formatli gelebildigi icin state'e normalize edilmis haliyle konur.
  const [mobilePhone, setMobilePhone] = useState(normalizePhone(data?.Info?.MobilePhone ?? ''))
  const [kvkkApproval, setKvkkApproval] = useState(data?.Info?.KvkkApproval ?? false)
  const [showKvkkText, setShowKvkkText] = useState(false)
  // Onay kutusu, metin en az bir kez acilana kadar devre disi. Daha once onaylamis
  // bayi icin okunmus sayilir; tekrar okutmaya zorlamiyoruz.
  const [kvkkTextOpened, setKvkkTextOpened] = useState(data?.Info?.KvkkApproval ?? false)

  // belgeler
  const [imzaSirkusu, setImzaSirkusu] = useState<IDealerContractFile | null>(null)
  const [vergiLevhasi, setVergiLevhasi] = useState<IDealerContractFile | null>(null)
  // Bu oturumda belge yuklendiyse, forma geri donup bilgi degistirirken tekrar dosya istenmez.
  // (data sayfa yuklenirken cekildigi icin Documents listesi bayat olabiliyor.)
  const [documentsUploaded, setDocumentsUploaded] = useState(false)

  // sözleşme onayı
  const [contractRead, setContractRead] = useState(false)
  const [smsSent, setSmsSent] = useState(false)
  const [smsCode, setSmsCode] = useState('')

  // temsilciler
  const [showRepresentatives, setShowRepresentatives] = useState(false)
  const [representatives, setRepresentatives] = useState<IRepresentatives[]>([])

  useEffect(() => {
    if (!data) return
    setStep(data.Step)
    setContractFileUrl(data.ContractFileUrl ?? null)
    setAuthorizedNameSurname(data.Info?.AuthorizedNameSurname ?? '')
    setAuthorizedTc(data.Info?.AuthorizedTc ?? '')
    setMersisNo(data.Info?.MersisNo ?? '')
    setHasNoMersis(data.Info?.HasNoMersis ?? false)
    setCommercialMessage(data.Info?.CommercialMessage ? 1 : -1)
    setEmail(data.Info?.Email ?? '')
    setMobilePhone(normalizePhone(data.Info?.MobilePhone ?? ''))
    setKvkkApproval(data.Info?.KvkkApproval ?? false)
    setKvkkTextOpened(data.Info?.KvkkApproval ?? false)
  }, [data])

  // Eksik bayi bilgisi varsa bayinin yapacagi tek sey temsilciye ulasmak; listeyi hemen getir.
  useEffect(() => {
    if ((data?.MissingDealerFields?.length ?? 0) > 0) {
      loadRepresentatives()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.MissingDealerFields?.length])

  const loadRepresentatives = useCallback(async () => {
    if (representatives.length > 0) {
      setShowRepresentatives(true)
      return
    }
    try {
      const api = new GetRepresentativesApi({})
      const res = await api.getRepresentatives()

      // Header.tsx ile ayni sirket sirasi; listede olmayanlar sona eklenir.
      const order = ['Index', 'Datagate', 'Despec', 'Netex']
      const sorted = [...(res ?? [])].sort((a, b) => {
        const ai = order.indexOf(a.Company?.Name)
        const bi = order.indexOf(b.Company?.Name)
        return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi)
      })

      setRepresentatives(sorted)
    } catch {
      setRepresentatives([])
    }
    setShowRepresentatives(true)
  }, [representatives.length])

  const rejectScopeText = useMemo(() => {
    switch (data?.RejectScope) {
      case DealerContractRejectScope.Belge:
        return 'Belge'
      case DealerContractRejectScope.Bilgi:
        return 'Bilgi'
      case DealerContractRejectScope.BelgeVeBilgi:
        return 'Belge ve Bilgi'
      default:
        return null
    }
  }, [data?.RejectScope])

  /**
   * Kayittaki belgeler bu an icin gecerli mi? Backend AreDocumentsUsable kurali:
   * belge kapsamiyla reddedilmisse eski belgeler sayilmaz, yeniden yuklenmeli.
   * Yalniz "Bilgi" reddinde mevcut belgeler gecerli kalir; bayiye tekrar yuklettirmeyiz.
   */
  const documentsAlreadyValid = useMemo(() => {
    if (!data) return false

    const rejectionNeedsDocuments =
      data.Status === DealerContractStatus.Reddedildi &&
      (data.RejectScope === DealerContractRejectScope.Belge ||
        data.RejectScope === DealerContractRejectScope.BelgeVeBilgi)

    return (data.Documents?.length ?? 0) >= 2 && !rejectionNeedsDocuments
  }, [data])

  const missingDealerFields = data?.MissingDealerFields ?? []
  const hasMissingDealerFields = missingDealerFields.length > 0

  const error = (text: string) =>
    Swal.fire({ icon: 'error', title: 'Hata', text, showConfirmButton: true, allowOutsideClick: false })

  /**
   * IPFile, onInput prop'unu yok sayip kendi handler'indan handleFiles(FileList) cagiriyor;
   * bu yuzden base64'e cevirme burada yapilir.
   */
  const readFile = async (fileList: any, label: string): Promise<IDealerContractFile | null> => {
    const file: File | undefined = fileList && fileList.length > 0 ? fileList[0] : undefined
    if (!file) return null

    // IPFile multiple={false} verilse bile input'u multiple aciyor; tek dosya bekliyoruz.
    if (fileList.length > 1) {
      error('Her belge için tek dosya seçiniz.')
      return null
    }

    // Uzanti MIME yerine dosya adindan alinir; backend uzantiya gore klasor sechiyor.
    const extension = (file.name.split('.').pop() ?? '').toLowerCase()

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      error('Yalnızca pdf, jpg, jpeg ve png dosyaları yüklenebilir.')
      return null
    }
    if (file.size > MAX_FILE_SIZE_BYTE) {
      error('Dosya boyutu en fazla 10 MB olabilir.')
      return null
    }

    const dataUrl = await handleImageUpload(file)

    // Label sabit: backend dosya adini bu etiketten uretiyor (tek nokta kurali).
    return { Label: label, Content: dataUrl.split(',')[1], Extension: extension, FileType: file.type }
  }

  const validateInfo = (): string | null => {
    const name = authorizedNameSurname.trim()
    if (name.length < 3 || name.length > 150) return 'Yetkili ad soyad 3-150 karakter olmalıdır.'

    if (!/^\d{11}$/.test(authorizedTc.trim())) return 'Yetkili TC kimlik numarası 11 haneli olmalıdır.'

    const mersis = mersisNo.trim()
    if (hasNoMersis) {
      if (mersis.length > 0 && !/^\d{16}$/.test(mersis)) return 'Mersis numarası 16 haneli olmalıdır.'
    } else if (!/^\d{16}$/.test(mersis)) {
      return 'Mersis numarası 16 haneli olmalıdır. Mersis kaydınız yoksa ilgili kutuyu işaretleyiniz.'
    }

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email.trim())) return 'Geçerli bir e-posta adresi giriniz.'

    // SMS dogrulama kodu bu numaraya gidecegi icin cep formati zorunlu.
    if (!/^5\d{9}$/.test(normalizePhone(mobilePhone)))
      return 'Geçerli bir cep telefonu numarası giriniz (örn. (532)123-4567).'

    if (commercialMessage !== 0 && commercialMessage !== 1)
      return 'Ticari elektronik ileti onayı için Evet veya Hayır seçiniz.'

    // KVKK onayı olmadan gönderim yapılamaz (backend de reddeder).
    if (!kvkkTextOpened) return 'KVKK aydınlatma metnini açıp okumanız gerekmektedir.'
    if (!kvkkApproval) return 'KVKK aydınlatma metnini okuyup onaylamanız gerekmektedir.'

    return null
  }

  /** form adımı: bilgi + iki belge birlikte gönderilir, ardından sözleşme adımına geçilir. */
  const submitForm = async () => {
    const validation = validateInfo()
    if (validation) return error(validation)

    const selectedFiles = [imzaSirkusu, vergiLevhasi].filter(Boolean) as IDealerContractFile[]

    // Belgeler zaten gecerliyse (yalniz "Bilgi" reddi) ya da bu oturumda yuklendiyse
    // yeniden yukleme zorunlu degil.
    if (!documentsAlreadyValid && !documentsUploaded && selectedFiles.length < 2)
      return error('İmza Sirküsü ve Vergi Levhası belgelerinin ikisini de yüklemeniz gerekmektedir.')

    setBusy(true)
    try {
      const saveApi = new DealerContractSaveInfoApi({})
      const saved = await saveApi.saveInfo({
        AuthorizedNameSurname: authorizedNameSurname.trim(),
        AuthorizedTc: authorizedTc.trim(),
        MersisNo: mersisNo.trim(),
        HasNoMersis: hasNoMersis,
        Email: email.trim(),
        MobilePhone: normalizePhone(mobilePhone),
        CommercialMessage: commercialMessage === 1,
        KvkkApproval: kvkkApproval,
      })

      // Backend yuklemeyi mevcut belgelerle birlestirir; yalnizca degistirilenler gonderilir.
      if (selectedFiles.length > 0) {
        const uploadApi = new DealerContractUploadDocumentsApi({})
        await uploadApi.uploadDocuments({ Files: selectedFiles })
        setDocumentsUploaded(true)
      }

      setContractFileUrl(saved?.ContractFileUrl ?? null)
      setStep('contract')
    } catch (err: any) {
      error(err?.description ?? err?.message ?? 'İşlem tamamlanamadı. Lütfen tekrar deneyiniz.')
    } finally {
      setBusy(false)
    }
  }

  /** documents adımı: yalnız belge reddi. Sözleşme yeniden onaylatılmaz. */
  const submitDocumentsOnly = async () => {
    if (!imzaSirkusu || !vergiLevhasi)
      return error('İmza Sirküsü ve Vergi Levhası belgelerinin ikisini de yeniden yüklemeniz gerekmektedir.')

    setBusy(true)
    try {
      const uploadApi = new DealerContractUploadDocumentsApi({})
      await uploadApi.uploadDocuments({ Files: [imzaSirkusu, vergiLevhasi] })
      setDocumentsUploaded(true)

      await Swal.fire({
        icon: 'success',
        title: 'Belgeleriniz iletildi',
        text: 'Belgeleriniz kontrol edilmek üzere iletilmiştir. Onaylandığında siteyi kullanmaya başlayabilirsiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      setStep('wait')
    } catch (err: any) {
      error(err?.description ?? err?.message ?? 'Belgeler yüklenemedi. Lütfen tekrar deneyiniz.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Sozlesme adimindan bilgi formuna geri doner. Bayi telefon/e-posta gibi alanlari
   * duzeltip "Devam Et" ile sozlesmeyi yeniden urettirebilir; SaveInfo PDF'i yeniden
   * olusturup onceki onayi dusurdugu icin OTP adimi da bastan yapilir.
   */
  const backToForm = async () => {
    setBusy(true)
    try {
      // Kayit "duzenleniyor" isaretlenir: admin panelinde gorunur ve onay engellenir.
      const api = new DealerContractReopenApi({})
      await api.reopen()

      setStep('form')
      setSmsSent(false)
      setSmsCode('')
      setContractRead(false)
    } catch (err: any) {
      error(err?.description ?? err?.message ?? 'İşlem tamamlanamadı. Lütfen tekrar deneyiniz.')
    } finally {
      setBusy(false)
    }
  }

  const sendSms = async () => {
    setBusy(true)
    try {
      const api = new DealerContractSendSmsApi({})
      await api.sendSms()
      setSmsSent(true)
      Swal.fire({
        icon: 'success',
        title: 'Doğrulama kodu gönderildi',
        text: `${formatPhone(mobilePhone || data?.Info?.MobilePhone || '')} numaralı telefona doğrulama kodu gönderildi.`,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } catch (err: any) {
      error(err?.description ?? err?.message ?? 'SMS gönderilemedi. Lütfen tekrar deneyiniz.')
    } finally {
      setBusy(false)
    }
  }

  const confirmContract = async () => {
    if (!contractRead) return error('Sözleşmeyi okuduğunuzu ve onayladığınızı işaretlemeniz gerekmektedir.')
    if (smsCode.trim().length !== 6) return error('6 haneli doğrulama kodunu giriniz.')

    setBusy(true)
    try {
      const api = new DealerContractConfirmApi({})
      const res = await api.confirm({ SmsCode: smsCode.trim() })

      if (res?.SiteAccess) {
        await Swal.fire({
          icon: 'success',
          title: 'Sözleşmeniz onaylandı',
          text: 'Bayilik sözleşmeniz ve belgeleriniz tarafımıza ulaşmıştır. Siteyi kullanmaya başlayabilirsiniz.',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        props.onCompleted()
        return
      }

      await Swal.fire({
        icon: 'success',
        title: 'Bilgileriniz iletildi',
        text: 'Sözleşme ve belgeleriniz kontrol edilmek üzere iletilmiştir. Onaylandığında siteyi kullanmaya başlayabilirsiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      setStep('wait')
    } catch (err: any) {
      error(err?.description ?? err?.message ?? 'Sözleşme onayı tamamlanamadı. Lütfen tekrar deneyiniz.')
    } finally {
      setBusy(false)
    }
  }

  if (!data) return null

  const dealer = data.Dealer

  // colClass: unvan/adres gibi uzun degerler icin 2 kutuluk (col-lg-8) yer verilebilir.
  const readOnlyRow = (label: string, value?: string, colClass: string = 'col-lg-4') => {
    const isMissing = !value || value.trim().length === 0
    return (
      <div className={`${colClass} mb-2`} key={label}>
        <label className='form-label'>
          {label}
          {isMissing && <span style={{ color: '#c00', marginLeft: 6 }}>eksik</span>}
        </label>
        <IPInput
          type='text'
          className='form-control'
          style={isMissing ? { borderColor: '#c00', backgroundColor: '#fff5f5' } : undefined}
          value={value ?? ''}
          disabled
          readOnly
          onChange={() => { }}
        />
      </div>
    )
  }

  const documentInputs = (
    <div className='row'>
      <div className='col-lg-6 mb-3'>
        <label className='form-label'>
          İmza Sirküsü {!documentsAlreadyValid && <span style={{ color: 'red' }}>*</span>}
        </label>
        <IPFile
          id='dc-imza-sirkusu'
          multiple={false}
          removeAfterSelect
          hideSelectedFilesList
          className='btn btn-primary'
          label='Dosya Seç'
          accept='.pdf,.jpg,.jpeg,.png'
          handleFiles={async (files: any) => setImzaSirkusu(await readFile(files, DEALER_CONTRACT_DOCUMENT_KEYS.ImzaSirkusu))}
        />
        <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
          {imzaSirkusu
            ? `✅ Seçildi (.${imzaSirkusu.Extension})`
            : documentsAlreadyValid
              ? 'Yüklü belgeniz geçerli — değiştirmek isterseniz seçin'
              : 'pdf, jpg, png — en fazla 10 MB'}
        </div>
      </div>
      <div className='col-lg-6 mb-3'>
        <label className='form-label'>
          Vergi Levhası {!documentsAlreadyValid && <span style={{ color: 'red' }}>*</span>}
        </label>
        <IPFile
          id='dc-vergi-levhasi'
          multiple={false}
          removeAfterSelect
          hideSelectedFilesList
          className='btn btn-primary'
          label='Dosya Seç'
          accept='.pdf,.jpg,.jpeg,.png'
          handleFiles={async (files: any) => setVergiLevhasi(await readFile(files, DEALER_CONTRACT_DOCUMENT_KEYS.VergiLevhasi))}
        />
        <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
          {vergiLevhasi
            ? `✅ Seçildi (.${vergiLevhasi.Extension})`
            : documentsAlreadyValid
              ? 'Yüklü belgeniz geçerli — değiştirmek isterseniz seçin'
              : 'pdf, jpg, png — en fazla 10 MB'}
        </div>
      </div>
    </div>
  )

  const rejectBanner = data.Status === DealerContractStatus.Reddedildi && (
    <div
      className='alert alert-danger'
      style={{ textAlign: 'left', marginBottom: 16 }}
    >
      <b>Belgeleriniz onaylanmadı{rejectScopeText ? ` (${rejectScopeText})` : ''}.</b>
      {data.RejectReason ? (
        <div style={{ marginTop: 6 }}>
          <b>Gerekçe:</b> {data.RejectReason}
        </div>
      ) : null}
      <div style={{ marginTop: 6 }}>
        Düzeltmeniz onaylanana kadar siteyi kullanamazsınız.
      </div>
    </div>
  )

  const dealerInfoPanel = (
    <>
      <div className='row' style={{ textAlign: 'left' }}>
        {readOnlyRow('Ünvan', dealer?.CompanyName, 'col-lg-8')}
        {readOnlyRow('Vergi / TC No', dealer?.TaxOrTcNumber)}
        {readOnlyRow('Adres', dealer?.Address, 'col-lg-8')}
        {readOnlyRow('Vergi Dairesi', dealer?.TaxAdministration)}
      </div>
      {hasMissingDealerFields && (
        <div style={{ textAlign: 'left', marginBottom: 12 }}>
          <span style={{ fontSize: '0.9rem' }}>
            Eksik veya hatalı bilgilerinizi kendiniz güncelleyemezsiniz;{' '}
            <span
              style={{ color: '#ec4c4c', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={loadRepresentatives}
            >
              Bayi temsilcinizle iletişime geçin
            </span>
            .
          </span>
        </div>
      )}
      {showRepresentatives && (
        <div style={{ textAlign: 'left', marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Bayi Temsilcileriniz</div>

          {representatives.length === 0 ? (
            <div className='alert alert-warning'>Temsilci bilgisi bulunamadı.</div>
          ) : (
            <div style={{ border: '1px solid #dedede', borderRadius: 8, padding: 12 }}>
              {representatives.map((item, companyIndex) => (
                <div
                  key={item.Company?.Id ?? `company-${companyIndex}`}
                  style={{
                    paddingBottom: 12,
                    marginBottom: 12,
                    borderBottom:
                      companyIndex < representatives.length - 1 ? '1px solid #dedede' : 'none',
                  }}
                >
                  {/* Sirket basligi: logo varsa logo, yoksa ad */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                    <div
                      style={{
                        border: '1px solid #dedede',
                        borderRadius: 24,
                        padding: '6px 28px',
                        minWidth: 200,
                        textAlign: 'center',
                      }}
                    >
                      {item.Company?.Image ? (
                        <img
                          src={item.Company.Image}
                          alt={item.Company?.Name ?? ''}
                          style={{ height: 22, objectFit: 'contain' }}
                        />
                      ) : (
                        <span style={{ fontWeight: 600 }}>{item.Company?.Name}</span>
                      )}
                    </div>
                  </div>

                  {/* Her temsilci tipi ayri kolonda */}
                  <div className='row'>
                    {(item.Representatives ?? []).map((rep, repIndex) => (
                      <div className='col-lg-4 mb-2' key={`${item.Company?.Id}-${repIndex}`}>
                        <div style={{ fontWeight: 600 }}>{rep.Type}</div>
                        <div>{rep.NameAndSurname}</div>
                        {rep.Email && (
                          <div>
                            <a href={`mailto:${rep.Email}`} style={{ color: '#c00' }}>
                              {rep.Email}
                            </a>
                          </div>
                        )}
                        {((rep.TelNumber ?? []) as string[]).map((tel, telIndex) => (
                          <div key={`tel-${telIndex}`}>
                            <a href={`tel:${normalizePhone(tel)}`} style={{ color: 'inherit' }}>
                              {formatPhone(tel)}
                            </a>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )

  /** Baslik gorselinin uzerinde gosterilen, adima gore degisen aciklama. */
  const headerSubtitle = (): JSX.Element => {
    if (step === 'representative')
      return <>Bilgileriniz eksik olduğu için sözleşme adımına geçilemiyor.</>

    if (step === 'wait')
      return <>Sözleşme ve belgeleriniz tarafımıza ulaştı; inceleme tamamlanana kadar bekleyiniz.</>

    if (step === 'documents')
      return <>Sözleşme onayınız geçerlidir; yalnızca belgelerinizi yeniden yüklemeniz gerekmektedir.</>

    if (step === 'contract')
      return <>Bilgileriniz ve belgeleriniz kaydedildi. Sözleşmeyi inceleyip SMS ile onaylamanız gerekmektedir.</>

    return (
      <>
        Bayilik sözleşmenizi onaylamanız ve <b>İmza Sirküsü</b> ile <b>Vergi Levhası</b> belgelerinizi
        yüklemeniz gerekmektedir.
        <br />
        Bu adımlar tamamlanmadan siteyi kullanamazsınız.
      </>
    )
  }

  const renderBody = () => {
    // TableDealers'taki zorunlu bilgi eksik: bayi bunu duzeltemez, akis ilerletilemez.
    if (step === 'representative') {
      return (
        <div style={{ textAlign: 'left' }}>
          <div className='alert alert-danger'>
            <b>Bilgileriniz eksik olduğu için sözleşme adımına geçilemiyor.</b>
            <div style={{ marginTop: 6 }}>
              {data.Message ??
                'Sistemde kayıtlı olması gereken bazı bilgileriniz eksik. Lütfen bayi temsilcinizle iletişime geçiniz.'}
            </div>
            {missingDealerFields.length > 0 && (
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {missingDealerFields.map(field => (
                  <li key={field}>{field}</li>
                ))}
              </ul>
            )}
          </div>
          {dealerInfoPanel}
        </div>
      )
    }

    if (step === 'wait') {
      return (
        <div style={{ textAlign: 'left' }}>
          {rejectBanner}
          <div className='alert alert-warning'>
            <b>Belgeleriniz inceleme aşamasındadır.</b>
            <div style={{ marginTop: 6 }}>
              Sözleşme ve belgeleriniz tarafımıza ulaşmıştır. Kontrol tamamlanıp onay verildiğinde siteyi
              kullanmaya başlayabilirsiniz. Bu süreçte ödeme ve sipariş işlemleri yapılamaz.
            </div>
          </div>
          {dealerInfoPanel}
        </div>
      )
    }

    if (step === 'documents') {
      return (
        <div>
          {rejectBanner}
          {documentInputs}
          {dealerInfoPanel}
        </div>
      )
    }

    if (step === 'contract') {
      return (
        <div>

          <div style={{ textAlign: 'left', marginBottom: 12, fontSize: '0.9rem', color: '#666' }}>
            Sözleşmedeki bilgiler hatalıysa <b>Bilgilerimi Düzelt</b> ile forma dönebilir, telefon ve
            e-posta dahil alanları değiştirip sözleşmeyi yeniden oluşturabilirsiniz.
          </div>

          {contractFileUrl ? (
            <>
              <iframe
                title='Bayilik Sözleşmesi'
                src={contractFileUrl}
                style={{ width: '100%', height: 420, border: '1px solid #ddd', marginBottom: 8 }}
              />
              <div style={{ textAlign: 'left', marginBottom: 12 }}>
                <a href={contractFileUrl} target='_blank' rel='noreferrer'>
                  Sözleşmeyi yeni sekmede aç / indir
                </a>
              </div>
            </>
          ) : (
            <div className='alert alert-danger'>Sözleşme belgesi görüntülenemedi. Lütfen sayfayı yenileyiniz.</div>
          )}

          <div style={{ textAlign: 'left', marginBottom: 12 }}>
            <label style={{ cursor: 'pointer' }}>
              <input
                type='checkbox'
                checked={contractRead}
                onChange={e => setContractRead(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Bayilik sözleşmesini okudum, onaylıyorum.
            </label>
          </div>

          {!data.CanSendSms ? (
            <div className='alert alert-danger' style={{ textAlign: 'left' }}>
              Sistemde kayıtlı telefon numarası bulunmadığından doğrulama kodu gönderilemiyor. Lütfen sayfanızı
              yeniledikten sonra ‘Bilgilerimi Düzelt’ butonu aracılığıyla telefon numaranızı kaydediniz.
            </div>
          ) : (
            <div className='row align-items-end' style={{ textAlign: 'left' }}>
              <div className='col-lg-5 mb-2'>
                <label className='form-label'>Doğrulama kodunun gönderileceği telefon</label>
                <IPInput
                  type='text'
                  className='form-control'
                  value={formatPhone(mobilePhone || data.Info?.MobilePhone || '') || '-'}
                  disabled
                  readOnly
                  onChange={() => { }}
                />
              </div>
              <div className='col-lg-3 mb-2'>
                <IPButton
                  type='button'
                  className='btn btn-primary'
                  text={smsSent ? 'Kodu Tekrar Gönder' : 'Doğrulama Kodu Gönder'}
                  disabled={busy}
                  onClick={sendSms}
                />
              </div>
              <div className='col-lg-4 mb-2'>
                <label className='form-label'>SMS Doğrulama Kodu</label>
                <IPInput
                  type='text'
                  className='form-control'
                  maxLength={6}
                  value={smsCode}
                  onChange={e => setSmsCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>
          )}

          {dealerInfoPanel}
        </div>
      )
    }

    // step === 'form'
    return (
      <div>
        {rejectBanner}

        {dealerInfoPanel}

        <div className='row' style={{ textAlign: 'left' }}>
          <div className='col-lg-6 mb-3'>
            <label className='form-label'>
              Yetkili Ad Soyad <span style={{ color: 'red' }}>*</span>
            </label>
            <IPInput
              type='text'
              className='form-control'
              maxLength={150}
              value={authorizedNameSurname}
              onChange={e => setAuthorizedNameSurname(e.target.value)}
            />
          </div>
          <div className='col-lg-6 mb-3'>
            <label className='form-label'>
              Yetkili TC Kimlik No <span style={{ color: 'red' }}>*</span>
            </label>
            <IPInput
              type='text'
              className='form-control'
              maxLength={11}
              value={authorizedTc}
              onChange={e => setAuthorizedTc(e.target.value.replace(/\D/g, ''))}
            />
            <div style={{ fontSize: '0.8rem' }}>
              Vergi numaranız ile ön-dolu gelir. Yetkili TC kimlik numarası <b>11 haneli</b> olmalıdır;
              farklıysa düzeltiniz.
            </div>
          </div>
          <div className='col-lg-6 mb-3'>
            <label className='form-label'>
              Mersis No <span style={{ color: 'red' }}>*</span>
            </label>
            <IPInput
              type='text'
              className='form-control'
              maxLength={16}
              value={mersisNo}
              disabled={hasNoMersis}
              onChange={e => setMersisNo(e.target.value.replace(/\D/g, ''))}
            />
            <label style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
              <input
                type='checkbox'
                checked={hasNoMersis}
                onChange={e => {
                  setHasNoMersis(e.target.checked)
                  if (e.target.checked) setMersisNo('')
                }}
                style={{ marginRight: 6 }}
              />
              Mersis kaydım yok
            </label>
          </div>
          <div className='col-lg-6 mb-3'>
            <label className='form-label'>
              E-posta <span style={{ color: 'red' }}>*</span>
            </label>
            <IPInput
              type='text'
              className='form-control'
              maxLength={150}
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <div style={{ fontSize: '0.8rem' }}>Sistemdeki bilgiyle ön-dolu gelir; hatalıysa düzeltiniz.</div>
          </div>
          <div className='col-lg-6 mb-3'>
            <label className='form-label'>
              Cep Telefonu <span style={{ color: 'red' }}>*</span>
            </label>
            <InputMask
              mask='(999)999-9999'
              name='dcMobilePhone'
              type='text'
              className='form-control'
              placeholder='(___)___-____'
              value={mobilePhone}
              onChange={e => setMobilePhone(e.target.value)}
            />
            <div style={{ fontSize: '0.8rem' }}>
              Sözleşme onayı için SMS doğrulama kodu bu numaraya gönderilecek.
            </div>
          </div>
          <div className='col-lg-6 mb-3'>
            <label className='form-label'>
              Ticari Elektronik İleti Onayı <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: 24 }}>
              <IPRadio
                text='Evet'
                name='dcCommercialMessage'
                onClick={() => setCommercialMessage(1)}
                checked={commercialMessage === 1}
              />
              <IPRadio
                text='Hayır'
                name='dcCommercialMessage'
                onClick={() => setCommercialMessage(0)}
                checked={commercialMessage === 0}
              />
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'left', marginBottom: 16 }}>
          <label className='form-label'>
            KVKK Aydınlatma Metni <span style={{ color: 'red' }}>*</span>
          </label>
          <div>
            <label style={{ cursor: kvkkTextOpened ? 'pointer' : 'not-allowed' }}>
              <input
                type='checkbox'
                checked={kvkkApproval}
                disabled={!kvkkTextOpened}
                onChange={e => setKvkkApproval(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              <span style={{ color: kvkkTextOpened ? 'inherit' : '#888' }}>
                KVKK aydınlatma metnini okudum ve onaylıyorum.
              </span>
            </label>
            <span
              style={{ color: '#ec4c4c', cursor: 'pointer', marginLeft: 12, textDecoration: 'underline' }}
              onClick={() => {
                // Metnin acilmasi onay kutusunun on kosulu; bir kez acildiktan sonra geri alinmaz.
                setShowKvkkText(prev => !prev)
                setKvkkTextOpened(true)
              }}
            >
              {showKvkkText ? 'Metni kapat' : 'Metni oku'}
            </span>
          </div>
          {!kvkkTextOpened && (
            <div style={{ fontSize: '0.85rem', color: '#c00', marginTop: 4 }}>
              Onaylayabilmek için önce <b>Metni oku</b> bağlantısına tıklayıp aydınlatma metnini
              açmanız gerekmektedir.
            </div>
          )}
          {showKvkkText && (
            <iframe
              title='kvkk-aydinlatma-metni'
              src='/assetWeb/docs/kvkk.pdf'
              style={{ width: '100%', height: 360, border: '1px solid #ddd', marginTop: 8 }}
            />
          )}
        </div>

        {documentInputs}
      </div>
    )
  }

  const renderFooter = () => {
    // Remote oturumda hangi adimda olursa olsun atlanabilir.
    const skipButton = props.allowSkip ? (
      <IPButton
        type='button'
        text='Geç (Remote)'
        style={{ width: 'auto', padding: '15px 20px', backgroundColor: '#6c757d' }}
        className='modal-button'
        onClick={props.onCompleted}
      />
    ) : null

    if (step === 'representative') {
      return (
        <>
          <span style={{ fontSize: '0.9rem' }}>
            Bilgileriniz güncellendikten sonra sözleşme adımına devam edebilirsiniz.
          </span>
          {skipButton}
        </>
      )
    }

    if (step === 'wait') {
      // Onay bekleniyor: ilerletecek bir aksiyon yok, kapatma da yok.
      return (
        <>
          <span style={{ fontSize: '0.9rem' }}>Onay sonrası siteyi kullanmaya başlayabilirsiniz.</span>
          {skipButton}
        </>
      )
    }

    if (step === 'documents') {
      return (
        <>
          {skipButton}
          <IPButton
            type='button'
            text='Belgeleri Gönder'
            style={{ width: 'auto', padding: '15px 20px' }}
            className='modal-button'
            disabled={busy}
            onClick={submitDocumentsOnly}
          />
        </>
      )
    }

    if (step === 'contract') {
      return (
        <>
          {skipButton}
          <IPButton
            type='button'
            text='Bilgilerimi Düzelt'
            style={{ width: 'auto', padding: '15px 20px', backgroundColor: '#6c757d' }}
            className='modal-button'
            disabled={busy}
            onClick={backToForm}
          />
          <IPButton
            type='button'
            text='Sözleşmeyi Onayla'
            style={{ width: 'auto', padding: '15px 20px' }}
            className='modal-button'
            disabled={busy || !data.CanSendSms}
            onClick={confirmContract}
          />
        </>
      )
    }

    return (
      <>
        {skipButton}
        <IPButton
          type='button'
          text='Devam Et'
          style={{ width: 'auto', padding: '15px 20px' }}
          className='modal-button'
          disabled={busy}
          onClick={submitForm}
        />
      </>
    )
  }

  return ReactDOM.createPortal(
    // Kapatma davranışı bilinçli olarak yok: onClose, ESC ve dışarı tıklama bağlanmadı.
    //
    // 'enter-done' ZORUNLU: IPModal.scss'te .modal varsayilan olarak opacity:0 ve
    // pointer-events:none; gorunurlugu yalnizca .modal.enter-done veriyor. Bu sinif
    // normalde CSSTransition tarafindan ekleniyor, burada transition kullanmadigimiz
    // icin elle veriyoruz. Aksi halde modal DOM'a girer ama seffaf ve tiklanamaz olur.
    <div className='modal enter-done'>
      <div className='modal-content' style={{ width: 1300, maxWidth: '96%' }} onClick={e => e.stopPropagation()}>
        <div
          className='modal-header'
          style={{
            // Gorsel yoksa gradient gorunur; her iki durumda da metin beyaz zeminde okunur.
            backgroundImage: `linear-gradient(rgba(120,0,0,0.35), rgba(120,0,0,0.35)), url('${HEADER_IMAGE}')`,
            backgroundColor: '#b00000',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderTopLeftRadius: '1rem',
            borderTopRightRadius: '1rem',
            padding: '22px 24px',
            textAlign: 'center',
          }}
        >
          <div style={{ width: '100%' }}>
            <h4
              className='modal-title'
              style={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.9rem',
                textShadow: '0 2px 6px rgba(0,0,0,0.45)',
              }}
            >
              Bayilik Sözleşmesi ve Belge Onayı
            </h4>
            <div
              style={{
                color: '#fff',
                fontSize: '0.95rem',
                marginTop: 8,
                textShadow: '0 1px 4px rgba(0,0,0,0.45)',
              }}
            >
              {headerSubtitle()}
            </div>
            {props.allowSkip && (
              <div
                style={{
                  display: 'inline-block',
                  marginTop: 10,
                  padding: '2px 10px',
                  borderRadius: 12,
                  fontSize: '0.8rem',
                  color: '#fff',
                  backgroundColor: 'rgba(0,0,0,0.35)',
                }}
              >
                Remote oturum — bu adımı geçebilirsiniz
              </div>
            )}
          </div>
        </div>
        <div className='modal-body'>{renderBody()}</div>
        <div className='modal-footer'>{renderFooter()}</div>
      </div>
    </div>,
    document.getElementById('ip-modal')!
  )
}

export default DealerContractModal
